import { Button, ListItemIcon, MenuItem, Pagination, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useState } from "react";
import BgBox from "../../../components/BgBox";
import { customAlert } from "../../../components/notify";
import { useTranslation } from "react-i18next";
import { getLast30Days } from "../../../utils/format";

import QueryKey from '../../../QueryKey';
import { useQuery } from 'react-query';
import LoadingScreen from '../../../components/loadingScreen';
import TrkOpReportsTable from '../../../components/TrkOpReportsTable';
import TrkReqReptTable from '../../../components/TrkReqReptTable';
import { ArrowDropDown, ArrowDropUp, Description, FilterAlt, PictureAsPdf } from '@mui/icons-material';
import OptionMenu from '../../../components/OptionMenu';
import FilterMdl from "./FilterMdl";
import { getAllTrkOpRepts, getTrkOpReqRept } from "../../../services/trkOp-service";
import { trkOpReptExcel, trkOpReqExel } from "../../../services/excel-service";
import { trkOpReptPdf, genTrkOpReqRept } from "../../../services/pdf-service";
import { getAllReportConfigs } from "../../../services/bo-service";
import { useAuthContext } from "../../../context/AuthContext";
import { DEFAULT_PAGE_SIZE } from '../../../constants/pagination';
import RecordCount from "../../../components/RecordCount";
import CustomDropDown from "../../../components/CustomDropDown";
import trkOpReqReptFieldsArr from "../../../utils/trkOpReqReptFieldsArr";


function TrpOpReportsListPage() {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const limit = DEFAULT_PAGE_SIZE
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { today, priorDate } = getLast30Days();
  const [filterObj, setFilterObj] = useState({
    fromDate: priorDate,
    toDate: today
  });

  // SA-configured "Truck Request Report" column sets; null = default aggregate view
  const { data: rptCfgArr } = useQuery([QueryKey.reportConfigList, "trkOpReq"], () => getAllReportConfigs("trkOpReq"));
  const [rptCfg, setRptCfg] = useState(null);

  function handleRptCfgChange(e) {
    const { value } = e.target;
    setPageNumber(1);
    setRptCfg(rptCfgArr?.find((i) => i.id === value) || null);
  }

  // console.log({ ...filterObj })
  async function fetchTrkOpReports() {
    let res = await getAllTrkOpRepts({ limit, pageNumber, ...filterObj });

    if (filterObj.fromDate && filterObj.toDate) {
      let allRes = await getAllTrkOpRepts({ limit: 1000, pageNumber: 1, ...filterObj });
      if (allRes?.data) {
        const start = new Date(filterObj.fromDate).setHours(0, 0, 0, 0);
        const end = new Date(filterObj.toDate).setHours(23, 59, 59, 999);
        const filteredData = allRes.data.filter(item => {
          const itemDate = new Date(item.trkOpRegDate).setHours(0, 0, 0, 0);
          return itemDate >= start && itemDate <= end;
        });

        const startIndex = (pageNumber - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        return {
          totalLength: filteredData.length,
          data: paginatedData
        }
      }
    }
    return res;
  }

  const { isLoading, isError, error, data: trkOpData } = useQuery(
    [QueryKey.trkOpRepts, pageNumber, filterObj],
    fetchTrkOpReports, {
    keepPreviousData: true,
    enabled: !rptCfg
  });

  const { isLoading: isTrkReqLoading, isError: isTrkReqError, error: trkReqError, data: trkReqData } = useQuery(
    [QueryKey.trkOpReqRepts, pageNumber, filterObj],
    () => getTrkOpReqRept({ limit, pageNumber, ...filterObj }), {
    keepPreviousData: true,
    enabled: !!rptCfg
  });

  const [filterMdl, setFilterMdl] = useState(false);
  const handleFilterMdlClose = () => setFilterMdl(false);
  const handleFilterMdlOpen = () => setFilterMdl(true);
  const { setLoadingScreen } = useAuthContext();


  const columns = [
    { id: "trkOp", label: "Truck Operator", minWidth: 170 },
    { id: "trkRegNo", label: "Truck", minWidth: 50 },
    // { id: "truckType", label: "Truck Type", minWidth: 170 },
    { id: "totalAmount", label: "Total Amount", minWidth: 170 },
    { id: "pendingAmount", label: "Pending Amount", minWidth: 170 }
  ];

  function getCfgColumns() {
    try {
      const parsed = JSON.parse(rptCfg?.config);
      if (Array.isArray(parsed) && parsed.length > 0)
        return parsed;
    } catch (err) {
      // corrupt config JSON — fall back to the full field set
    }
    return trkOpReqReptFieldsArr;
  }

  const cfgColumns = rptCfg ? getCfgColumns() : null;


  const optionItems = [
    {
      title: t("bo.reptListPg.pdf"),
      icon: <PictureAsPdf fontSize="small" />,
      fn: async () => {
        setLoadingScreen(true);
        try {
          const res = rptCfg ?
            await genTrkOpReqRept({ filterObj, columns: cfgColumns }) :
            await trkOpReptPdf({ filterObj, columns });
          window.open(res.fileUrl, "_blank")
        } catch (err) {
          // console.log(err);
          customAlert(err)
        }
        setLoadingScreen(false);
      }
    },
    {
      title: t("bo.reptListPg.excel"),
      icon: <Description fontSize="small" />,
      fn: async () => {
        try {
          const res = rptCfg ?
            await trkOpReqExel({ filterObj, columns: cfgColumns }) :
            await trkOpReptExcel({ filterObj, columns });
          window.open(res.fileUrl, "_blank")
        } catch (err) {
          // console.log(err);
          customAlert(err)
        }
      }
    }
  ]


  const handlePageChange = async (event, value) => {
    setPageNumber(value);
  };

  function handleFilterObj(obj) {
    setPageNumber(1);
    setFilterObj({ ...obj })
  }



  if (rptCfg ? isTrkReqError : isError) {
    customAlert(rptCfg ? trkReqError : error);
    return <h2>Something went wrong</h2>
  }

  if (rptCfg ? isTrkReqLoading : isLoading)
    return <Box sx={{ position: "absolute", top: 0, left: 0 }}><LoadingScreen /></Box>


  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const totalLength = rptCfg ? trkReqData?.totalLength : trkOpData?.totalLength;

  return (

    <BgBox px={4} py={1}>
      <Stack direction={'row'} justifyContent="space-between" alignItems={"center"} mb={0.8} mt={2} px={2}>
        <Stack direction={'row'} alignItems={"center"}>
          <Typography variant='h5' sx={{ fontWeight: "bold", mr: 4, mb: 1 }}>{t("bo.trkOpReptsPg.trkOpRept")}<RecordCount count={totalLength} /></Typography>
        </Stack>

        <Stack direction={'row'} justifyContent="space-between" alignItems={"center"}>
          {rptCfgArr?.length > 0 &&
            <CustomDropDown key={rptCfg?.id || "default"} height={36} width={180} fontSize={13} handleDDChange={handleRptCfgChange} defaultValue={rptCfg?.id || "default"} ddArr={[{ value: "default", label: "Default" }, ...rptCfgArr.map((i) => ({ value: i.id, label: i.configName }))]} />}
          <Button variant='outlined' sx={{ ml: 2, mb: 0.4 }} startIcon={<FilterAlt />} color='primary' onClick={handleFilterMdlOpen} >{t("bo.reptListPg.filter")}</Button>
          <Button variant='contained' sx={{ ml: 2, mb: 0.4 }} endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />} color='primary' onClick={handleExportClick} >{t("bo.reptListPg.export")}</Button>

        </Stack>
      </Stack>

      {rptCfg ?
        <TrkReqReptTable trkReqData={trkReqData?.data} columns={cfgColumns} /> :
        <TrkOpReportsTable trkOpData={trkOpData?.data} columns={columns} />}


      <Stack sx={{ alignItems: "center", mt: 1 }}>
        <Pagination count={Math.ceil(totalLength / limit) || 0} color="primary" page={pageNumber} onChange={handlePageChange} variant="outlined" shape="rounded" />
      </Stack>
      <OptionMenu open={open} handleClose={handleExportClose} anchorEl={anchorEl} >
        {optionItems.map((i) => <MenuItem sx={{ pl: 2, pr: 5 }} key={i.title} onClick={i.fn}>
          <ListItemIcon > {i.icon}</ListItemIcon>
          {i.title}
        </MenuItem>)}
      </OptionMenu>

      <FilterMdl modalOpen={filterMdl} handleModalClose={handleFilterMdlClose} filterObj={filterObj} handleFilterObj={handleFilterObj} />
    </BgBox>

  )
}

export default TrpOpReportsListPage;